import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'next-themes';
import { Toaster } from '@/components/ui/toaster';
import { AuthProvider, useAuth } from '@/context/auth';
import { LanguageProvider, DEFAULT_LANGUAGE } from '@/context/language-context';
import { PresenceProvider } from '@/context/presence-context';
import { useLanguage } from '@/context/language-context';
import { canAccessSugarSection, canCreateSugarBabyProfile, canCreateSugarDaddyProfile } from '@/context/auth/auth-utils';

// Pages
import Index from '@/pages/Index';
import About from '@/pages/About';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Dashboard from '@/pages/Dashboard';
import Explore from '@/pages/Explore';
import ProfileDetail from '@/pages/ProfileDetail';
import Messages from '@/pages/Messages';
import Admin from '@/pages/Admin';
import NotFound from '@/pages/NotFound';
import HowItWorks from '@/pages/HowItWorks';
import Security from '@/pages/Security';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import ProfileManagement from '@/pages/ProfileManagement';
import Favorites from '@/pages/Favorites';
import Support from '@/pages/Support';
import Blog from '@/pages/Blog';
import Faq from '@/pages/Faq';
import Pricing from '@/pages/Pricing';
import SafetyTips from '@/pages/SafetyTips';
import ContentPolicy from '@/pages/ContentPolicy';
import Cookies from '@/pages/Cookies';
import Subscription from '@/pages/Subscription';
import EscortSubscription from '@/pages/EscortSubscription';
import EscortDashboard from '@/pages/EscortDashboard';
import EscortProfileManagerPage from '@/pages/EscortProfileManager';
import EscortCalendarManager from '@/pages/EscortCalendarManager';
import EscortMessagesManager from '@/pages/EscortMessagesManager';
import EscortMediaManager from '@/pages/EscortMediaManager';
import EscortFinancesManager from '@/pages/EscortFinancesManager';
import EscortSecurityManager from '@/pages/EscortSecurityManager';
import RequireAuth from '@/components/auth/RequireAuth';
import SugarRelationshipsPage from '@/pages/SugarRelationshipsPage';
import SugarBabyDetailPage from '@/pages/SugarBabyDetailPage';
import SugarDaddyDetailPage from '@/pages/SugarDaddyDetailPage';
import CreateSugarBabyProfilePage from '@/pages/CreateSugarBabyProfilePage';
import EditSugarBabyProfilePage from '@/pages/EditSugarBabyProfilePage';
import CreateSugarDaddyProfilePage from '@/pages/CreateSugarDaddyProfilePage';
import EditSugarDaddyProfilePage from '@/pages/EditSugarDaddyProfilePage';
import SugarTeaserPage from '@/pages/SugarTeaserPage';
import EscortBookingsPage from '@/pages/EscortBookingsPage';
import EscortPremiumPage from '@/pages/EscortPremiumPage';

/**
 * Root layout component for wrapping routes with language context - 11
 */
const RoutesWithLanguage = () => {
  const { defaultLanguage, currentLanguage, supportedLanguages } = useLanguage();
  const { user } = useAuth();
  
  // Create regex pattern for supported language prefixes
  const langPattern = supportedLanguages.join('|');
  
  return (
    <Routes>
      {/* Root route - Redirect to default language */}
      <Route 
        path="/" 
        element={<Navigate to={`/${defaultLanguage}`} replace />} 
      />

      {/* Dynamic language routes */}
      <Route path="/:lang">
        {/* Index route */}
        <Route index element={<Index />} />
        
        {/* Common routes */}
        <Route path="about" element={<About />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="explore" element={<Explore />} />
        <Route path="profile/:id" element={<ProfileDetail />} />
        <Route path="messages/:conversationId?" element={<Messages />} />
        <Route path="admin" element={<Admin />} />
        <Route path="how-it-works" element={<HowItWorks />} />
        <Route path="security" element={<Security />} />
        <Route path="terms" element={<Terms />} />
        <Route path="privacy" element={<Privacy />} />
        <Route path="profile-management" element={<ProfileManagement />} />
        <Route path="favorites" element={<Favorites />} />
        <Route path="support" element={<Support />} />
        <Route path="blog" element={<Blog />} />
        <Route path="faq" element={<Faq />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="safety-tips" element={<SafetyTips />} />
        <Route path="content-policy" element={<ContentPolicy />} />
        <Route path="cookies" element={<Cookies />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="escort-subscription" element={<EscortSubscription />} />
        
        {/* --- Public Sugar Dating Teaser Route --- */}
        <Route path="sugar-teaser" element={<SugarTeaserPage />} /> 
        
        {/* --- PROTECTED Sugar Relationships Routes --- */}
        <Route 
          path="sugar-relationships" 
          element={
            <RequireAuth requirePermission={canAccessSugarSection}>
              <SugarRelationshipsPage />
            </RequireAuth>
          }
        />
        <Route 
          path="sugar-relationships/sb/:profileId"
          element={
            <RequireAuth requirePermission={canAccessSugarSection}>
              <SugarBabyDetailPage />
            </RequireAuth>
          }
        />
         <Route 
          path="sugar-relationships/sd/:profileId"
          element={
            <RequireAuth requirePermission={canAccessSugarSection}>
              <SugarDaddyDetailPage />
            </RequireAuth>
          }
        />
        <Route 
          path="sugar-relationships/create-sb" 
          element={
            <RequireAuth requirePermission={canCreateSugarBabyProfile}>
              <CreateSugarBabyProfilePage />
            </RequireAuth>
          }
        />
         <Route 
          path="sugar-relationships/edit-sb" 
          element={
            <RequireAuth requirePermission={(u) => canCreateSugarBabyProfile(u) && !!u?.sugarBabyProfileId}>
              <EditSugarBabyProfilePage />
            </RequireAuth>
          }
        />
         <Route 
          path="sugar-relationships/create-sd" 
          element={
            <RequireAuth requirePermission={canCreateSugarDaddyProfile}>
              <CreateSugarDaddyProfilePage />
            </RequireAuth>
          }
        />
         <Route 
          path="sugar-relationships/edit-sd" 
          element={
            <RequireAuth requirePermission={(u) => canCreateSugarDaddyProfile(u) && !!u?.sugarDaddyProfileId}>
              <EditSugarDaddyProfilePage />
            </RequireAuth>
          }
        />
        
        {/* Dashboard Escort Routes */}
        <Route 
          path="escort-dashboard" 
          element={
            <RequireAuth requireRole="escort">
              <EscortDashboard />
            </RequireAuth>
          } 
        />
        <Route 
          path="escort-dashboard/profile" 
          element={
            <RequireAuth requireRole="escort">
              <EscortProfileManagerPage />
            </RequireAuth>
          } 
        />
        <Route 
          path="escort-dashboard/calendar" 
          element={
            <RequireAuth requireRole="escort">
              <EscortCalendarManager />
            </RequireAuth>
          } 
        />
        <Route 
          path="escort-dashboard/messages" 
          element={
            <RequireAuth requireRole="escort">
              <EscortMessagesManager />
            </RequireAuth>
          } 
        />
        <Route 
          path="escort-dashboard/media" 
          element={
            <RequireAuth requireRole="escort">
              <EscortMediaManager />
            </RequireAuth>
          } 
        />
        <Route 
          path="escort-dashboard/bookings"
          element={
            <RequireAuth requireRole="escort">
              <EscortBookingsPage />
            </RequireAuth>
          }
        />
        <Route 
          path="escort-dashboard/finances" 
          element={
            <RequireAuth requireRole="escort">
              <EscortFinancesManager />
            </RequireAuth>
          } 
        />
        <Route 
          path="escort-dashboard/premium"
          element={
            <RequireAuth requireRole="escort">
              <EscortPremiumPage />
            </RequireAuth>
          }
        />
        <Route 
          path="escort-dashboard/security" 
          element={
            <RequireAuth requireRole="escort">
              <EscortSecurityManager />
            </RequireAuth>
          } 
        />
      </Route>

      {/* Redirect unsupported direct paths to prefixed paths */}
      <Route path="*" element={<LanguageRedirect />} />
    </Routes>
  );
};

/**
 * Component to handle redirecting non-prefixed paths to language prefixed paths
 */
const LanguageRedirect = () => {
  const { currentLanguage, supportedLanguages } = useLanguage();
  const pathname = window.location.pathname;
  
  // Check if the path already starts with a language code
  const hasLangPrefix = supportedLanguages.some(lang => 
    pathname.startsWith(`/${lang}/`) || pathname === `/${lang}`
  );
  
  // If path already has a lang prefix but wasn't matched, it's truly not found
  if (hasLangPrefix) {
    return <NotFound />;
  }
  
  // Otherwise redirect to prefixed path
  return <Navigate to={`/${currentLanguage}${pathname}`} replace />;
};

function App() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <ThemeProvider>
        <AuthProvider>
          <PresenceProvider>
            <BrowserRouter>
              <LanguageProvider>
                <RoutesWithLanguage />
                <Toaster />
              </LanguageProvider>
            </BrowserRouter>
          </PresenceProvider>
        </AuthProvider>
      </ThemeProvider>
    </Suspense>
  );
}

export default App;
